import React, { useState, useEffect } from "react";
import Sidebar, { SIDEBAR_WIDTH } from "./Sidebar";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function AdminDashboardLayout() {
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const saved = localStorage.getItem('adminSidebarVisible');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isArabic, setIsArabic] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Verify admin access on mount
  useEffect(() => {
    checkAdminAccess();
  }, []);

  async function checkAdminAccess() {
    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.log('No authenticated user, redirecting to signin');
        navigate('/signin');
        return;
      }

      // Check if user is an admin
      const { data: adminProfile, error } = await supabase
        .from('admins')
        .select('id, email, full_name, is_active')
        .eq('auth_user_id', user.id)
        .single();

      if (error || !adminProfile || !adminProfile.is_active) {
        console.log('User is not an admin or account is inactive');
        setIsAdmin(false);
        // Redirect to appropriate dashboard based on user role
        const userRole = localStorage.getItem('userRole');
        if (userRole === 'student') {
          navigate('/dashboard/student/dashboard');
        } else if (userRole === 'teacher') {
          navigate('/dashboard/teacher/dashboard');
        } else {
          navigate('/');
        }
        return;
      }

      // User is an admin
      setIsAdmin(true);
      console.log('Admin access verified:', adminProfile.email);
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
      navigate('/signin');
    } finally {
      setLoading(false);
    }
  }

  // Persist sidebar state when it changes
  useEffect(() => {
    localStorage.setItem('adminSidebarVisible', JSON.stringify(sidebarVisible));
  }, [sidebarVisible]);

  // Handler for language change from Sidebar
  const handleLanguageChange = (arabic) => {
    console.log('AdminDashboardLayout: handleLanguageChange called with:', arabic);
    console.log('AdminDashboardLayout: Current isArabic before change:', isArabic);
    setIsArabic(arabic);
    // Save to localStorage to persist across pages
    localStorage.setItem('taealam_language', arabic ? 'ar' : 'en');
    // Update document direction
    document.documentElement.dir = arabic ? 'rtl' : 'ltr';
    document.documentElement.lang = arabic ? 'ar' : 'en';
    console.log('AdminDashboardLayout: isArabic state updated to:', arabic);
  };

  // Show loading state while checking admin access
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f7f9fb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #FF7426',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#666', fontSize: 14 }}>Verifying admin access...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Don't render dashboard if not admin
  if (isAdmin === false) {
    return null;
  }

  return (
    <div
      className={isArabic ? 'arabic-font' : ''}
      style={{ background: "#f7f9fb", minHeight: "100vh", width: "100%", position: 'relative' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Hamburger button to toggle sidebar */}
      <button
        aria-label={sidebarVisible ? (isArabic ? "إخفاء القائمة" : "Hide sidebar") : (isArabic ? "إظهار القائمة" : "Show sidebar")}
        onClick={() => setSidebarVisible((v) => !v)}
        style={{
          position: 'fixed',
          top: 18,
          left: isArabic ? 'auto' : 18,
          right: isArabic ? 18 : 'auto',
          zIndex: 2100,
          background: '#fff',
          border: 'none',
          borderRadius: 8,
          boxShadow: '0 2px 8px #eee',
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {sidebarVisible ? (
          // Close (X) icon
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          // Hamburger icon
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#232B3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </button>

      {sidebarVisible && (
        <Sidebar
          userRole="admin"
          isVisible={sidebarVisible}
          isOpen={true}
          setSidebarVisible={setSidebarVisible}
          isArabic={isArabic}
          onLanguageChange={handleLanguageChange}
        />
      )}

      <main
        style={{
          flex: 1,
          padding: 40,
          marginLeft: !isArabic && sidebarVisible ? SIDEBAR_WIDTH : 0,
          marginRight: isArabic && sidebarVisible ? SIDEBAR_WIDTH : 0,
          transition: "margin-left 0.3s, margin-right 0.3s",
        }}
      >
        <Outlet context={{ sidebarVisible, setSidebarVisible, isArabic, handleLanguageChange }} />
      </main>
    </div>
  );
}
